import type { ReactNode } from "react";
import { ArrowDown, ArrowUp, ChevronDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

export type VoltSortOption = {
  value: string;
  label: string;
  standard?: boolean;
};
export type VoltSortDirection = "asc" | "desc";

/** Sort order as a parameter, not a filter: direction button, "Sortieren nach" menu, optional extras after it. */
export const VoltSortMenu = ({
  options,
  value,
  onChange,
  direction,
  onDirectionChange,
  children,
  className,
}: {
  options: VoltSortOption[];
  value: string;
  onChange: (value: string) => void;
  direction: VoltSortDirection;
  onDirectionChange: (direction: VoltSortDirection) => void;
  children?: ReactNode;
  className?: string;
}) => {
  const current = options.find((o) => o.value === value) ?? options[0];
  const asc = direction === "asc";
  return (
    <div className={cn("flex items-center gap-1", className)}>
      <Button
        variant="outline"
        size="icon"
        aria-label={
          asc
            ? "Aufsteigend, umschalten auf absteigend"
            : "Absteigend, umschalten auf aufsteigend"
        }
        title={asc ? "Aufsteigend" : "Absteigend"}
        onClick={() => onDirectionChange(asc ? "desc" : "asc")}
      >
        {asc ? <ArrowUp /> : <ArrowDown />}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-9">
            Sortieren nach: {current?.label}
            <ChevronDown />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-56">
          <DropdownMenuLabel className="text-xs uppercase text-muted-foreground">
            Sortieren nach
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {options.map((o) => (
            <DropdownMenuCheckboxItem
              key={o.value}
              checked={o.value === current?.value}
              onCheckedChange={() => onChange(o.value)}
            >
              {o.label}
              {o.standard ? (
                <span className="text-muted-foreground">(Standard)</span>
              ) : null}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {children}
    </div>
  );
};
